import { createSlice } from '@reduxjs/toolkit'

const safeParseCart = () => {
  try {
    const raw = localStorage.getItem('allItemsCart')
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.map((item) => {
      const next = { ...item }
      delete next.timestamp
      return next
    })
  } catch {
    return []
  }
}

const initialState = {
  allItemsCart: safeParseCart(),
}

/** Update item by sku and move it to the end (recency). No-op if missing. */
export const bumpCartItem = (cart, sku, updater) => {
  const itemIndex = cart.findIndex((item) => item?.sku === sku)
  if (itemIndex < 0) return cart

  const current = cart[itemIndex]
  const updated = {
    ...updater({ ...current }),
    timestamp: Date.now(),
  }

  return [...cart.filter((item) => item.sku !== sku), updated]
}

const clampNonNegative = (value) => {
  const n = parseFloat(value)
  if (!n || n < 0) return 0
  return n
}

export const allItemsCartSlice = createSlice({
  name: 'allItemsCart',
  initialState,
  reducers: {
    setItemsCart: (state, action) => {
      state.allItemsCart = Array.isArray(action.payload) ? [...action.payload] : []
    },
    setAllItemsCartOptimistic: (state, action) => {
      const payloadItem = action.payload
      const itemIndex = state.allItemsCart.findIndex((item) => item.sku === payloadItem.sku)

      if (itemIndex > -1) {
        state.allItemsCart = state.allItemsCart.filter((item) => item.sku !== payloadItem.sku)
      } else {
        state.allItemsCart.push({
          ...payloadItem,
          quantity: 1,
          quantityOrder: 1,
          timestamp: Date.now(),
        })
      }
    },

    revertCartUpdate: (state, action) => {
      state.allItemsCart = Array.isArray(action.payload) ? action.payload : []
    },
    setClearCart: (state) => {
      state.allItemsCart = []
    },
    setAllItemsCart: (state, action) => {
      const exists = state.allItemsCart.some((elem) => elem.sku === action.payload.sku)
      if (!exists) {
        if (action.payload.quantity === 0) {
          state.allItemsCart.push({
            ...action.payload,
            quantity: 1,
            quantityOrder: 1,
            timestamp: Date.now(),
          })
        } else {
          state.allItemsCart.push(action.payload)
        }
      } else {
        state.allItemsCart = state.allItemsCart.filter((item) => item.sku !== action.payload.sku)
      }
    },
    setCartElemQuantityIncrement: (state, action) => {
      state.allItemsCart = bumpCartItem(state.allItemsCart, action.payload.sku, (item) => ({
        ...item,
        quantity: (parseFloat(item.quantity) || 0) + 1,
        quantityOrder: (parseFloat(item.quantityOrder) || 0) + 1,
      }))
    },
    setCartElemQuantityDecrement: (state, action) => {
      state.allItemsCart = bumpCartItem(state.allItemsCart, action.payload.sku, (item) => ({
        ...item,
        quantity: clampNonNegative((parseFloat(item.quantity) || 0) - 1),
        quantityOrder: clampNonNegative((parseFloat(item.quantityOrder) || 0) - 1),
      }))
    },
    setCartElemQuantityValue: (state, action) => {
      const sku = action.payload.elem?.sku || action.payload.sku
      state.allItemsCart = bumpCartItem(state.allItemsCart, sku, (item) => ({
        ...item,
        quantity: parseFloat(action.payload.value) || 0,
        quantityOrder: parseFloat(action.payload.value) || 0,
      }))
    },
    setCartElemQuantityOrderValue: (state, action) => {
      const sku = action.payload.elem?.sku || action.payload.sku
      state.allItemsCart = bumpCartItem(state.allItemsCart, sku, (item) => ({
        ...item,
        quantityOrder: action.payload.quantityOrder,
      }))
    },
    setCartElemCurrentUnit: (state, action) => {
      const sku = action.payload.elem?.sku || action.payload.sku
      state.allItemsCart = bumpCartItem(state.allItemsCart, sku, (item) => ({
        ...item,
        currentUnit: action.payload.value,
      }))
    },
    deleteCartElem: (state, action) => {
      state.allItemsCart = state.allItemsCart.filter((item) => item?.sku !== action.payload.sku)
    },
  },
})

export const {
  setClearCart,
  setItemsCart,
  setAllItemsCartOptimistic,
  revertCartUpdate,
  deleteCartElem,
  setAllItemsCart,
  setCartElemCurrentUnit,
  setCartElemQuantityDecrement,
  setCartElemQuantityIncrement,
  setCartElemQuantityValue,
  setCartElemQuantityOrderValue,
} = allItemsCartSlice.actions

export default allItemsCartSlice.reducer
